import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { generatePath, useHistory } from 'react-router-dom';

import type { IResourceFolderType } from '@/api/types';
import type { ICommonFolderColumnNames, ISimpleResourceType } from '@/components/common';

import {
  commonFolderSortingFunctions,
  getFolderChildren,
  getFolderType,
  getLabel,
  getSelectedFolderId,
} from '@/helpers';
import {
  Table,
  DropdownMenu,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  ClickableResourceTableCell,
} from '@/components/common';
import { useFolderModal, useResourceNavigation, useTableSort } from '@/hooks';
import { ResourceFolder } from '@/assets/icons';
import { FolderModalType } from '@/hooks/enums';
import { ResourcesNoData } from '@/components/common';
import routes from '@/config/routes';

interface IProps {
  items: IResourceFolderType[];
}

const ResourceFoldersTable = ({ items }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { selectedFolder } = useResourceNavigation();
  const { sortedData, onSort, setRawData, sortingState } = useTableSort<ICommonFolderColumnNames, IResourceFolderType>({
    initial: { name: 'DESC' },
    sortingFunctions: commonFolderSortingFunctions,
    rawData: items,
  });
  const { handleOpenModal } = useFolderModal();
  const history = useHistory();

  useEffect(() => {
    setRawData(items);
  }, [items, setRawData]);

  const isDocument = selectedFolder?.folderType === 'document';

  if (!items?.length) {
    return <ResourcesNoData label={t`resources.content.noFolders`} />;
  }

  return (
    <Table>
      <ResourceTableHeader className="small">
        <ResourceTableHeaderCell width="26px" minWidth="26px"></ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={3}
          sortingEnabled
          sortingState={sortingState.name}
          onClick={() => onSort('name')}
        >{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
        {isDocument && (
          <ResourceTableHeaderCell
            size={1}
            sortingEnabled
            sortingState={sortingState.agentVisible}
            onClick={() => onSort('agentVisible')}
          >{t`resources.foldersList.headerAgentVisible`}</ResourceTableHeaderCell>
        )}
        <ResourceTableHeaderCell
          size={1}
          sortingEnabled
          sortingState={sortingState.items}
          onClick={() => onSort('items')}
        >{t`resources.foldersList.headerItems`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell width="26px" minWidth="26px" className="p-0 ms-3"></ResourceTableHeaderCell>
      </ResourceTableHeader>
      {sortedData.map((item, id) => (
        <ResourceTableRow className="small" key={id}>
          <ResourceTableCell width="26px" minWidth="26px" className="m-0">
            <FolderIcon src={ResourceFolder} />
          </ResourceTableCell>
          <ClickableResourceTableCell
            size={3}
            onClick={() => {
              if (selectedFolder?.clientId) {
                history.push(
                  generatePath(routes.sp.scheduling.resources.folder, {
                    client: selectedFolder?.clientId,
                    resourceType: getFolderType(item),
                    folderId: item.folderId,
                  }),
                );
              }
            }}
          >
            {getLabel(item)}
          </ClickableResourceTableCell>
          {isDocument && (
            <ResourceTableCell size={1}>{item.agentVisible ? t`common.btnYes` : t`common.btnNo`}</ResourceTableCell>
          )}
          <ResourceTableCell size={1}>{getFolderChildren(item).length}</ResourceTableCell>
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu
              options={[
                {
                  label: t`common.btnEdit`,
                  callback: () =>
                    selectedFolder &&
                    handleOpenModal({
                      modalType: FolderModalType.edit,
                      data: {
                        folderType: getFolderType(item) as ISimpleResourceType,
                        clientId: selectedFolder.clientId,
                        name: item.name,
                        agentVisible: item.agentVisible,
                        parentFolderId: getSelectedFolderId(selectedFolder),
                        folderId: item.folderId,
                      },
                    }),
                },
                {
                  label: t`common.btnDelete`,
                  callback: () =>
                    selectedFolder &&
                    handleOpenModal({
                      modalType: FolderModalType.delete,
                      data: {
                        folderType: getFolderType(item) as ISimpleResourceType,
                        clientId: selectedFolder.clientId,
                        name: item.name,
                        parentFolderId: getSelectedFolderId(selectedFolder),
                        folderId: item.folderId,
                      },
                    }),
                },
              ]}
            />
          </ResourceTableCell>
        </ResourceTableRow>
      ))}
    </Table>
  );
};

export { ResourceFoldersTable };

const FolderIcon = styled.img`
  width: 100%;
  max-height: 16px;
`;
